import { ArrowUpDown } from "lucide-react";
import type React from "react";

import { emitFilterSort } from "~/hec/analytics/event-layer/gtm-filter-events";
import {
  TOOLBAR_ICON_SIZE,
  toolbarTriggerClass,
} from "~/hec/components/plp-toolbar/toolbar-trigger";
import {
  SET_SELECTED_SORT_OPTION,
  useCioFacetSelector,
} from "~/hec/context/cio-facet-adapter";

interface SortPopoverProps {
  "data-testid"?: string;
}

const displayNameMap = (displayName: string): string => {
  return displayName === "Best Matches" ? "Best Sellers" : displayName;
};

export const SortPopover: React.FC<SortPopoverProps> = ({
  "data-testid": dataTestId,
}) => {
  const { sortOptions } = useCioFacetSelector(c => c.state);
  const dispatch = useCioFacetSelector(c => c.dispatch);

  const hasOptions = !!sortOptions && sortOptions.length > 0;
  const selectedI = sortOptions.findIndex(o => o.status === "selected");

  const onChange: React.ChangeEventHandler<HTMLSelectElement> = e => {
    const next = sortOptions[+e.target.value];
    if (next) {
      dispatch({ type: SET_SELECTED_SORT_OPTION, payload: next });
      // GTM `filterSort` — mirrors the prod container's sort listener
      // (`{event:'filterSort', selectedElement:<option text>}`); the container's
      // own select#sort-order-select script can't fire on this DOM.
      emitFilterSort(displayNameMap(next.display_name));
    }
  };

  return (
    <label
      className={`relative ${toolbarTriggerClass} aria-disabled:cursor-not-allowed aria-disabled:opacity-50`}
      aria-disabled={!hasOptions || undefined}
      data-testid={dataTestId || "sortPopover"}
    >
      <ArrowUpDown
        width={TOOLBAR_ICON_SIZE}
        height={TOOLBAR_ICON_SIZE}
        data-testid={dataTestId || "sortPopover-SortArrow"}
        strokeWidth={1.25}
      />

      <span>Sort</span>
      <select
        aria-label="Sort"
        disabled={!hasOptions}
        value={selectedI}
        onChange={onChange}
        className="absolute inset-0 cursor-pointer opacity-0 focus:outline-none disabled:cursor-not-allowed"
        data-testid={dataTestId || "sortPopover-select"}
      >
        {sortOptions.map((o, i) => (
          <option key={o.display_name + i} value={i}>
            {displayNameMap(o.display_name)}
          </option>
        ))}
      </select>
    </label>
  );
};
